// src/pages/PromoPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { db } from "../firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import {
    getActivePromo,
    getPromo,
    getActivePromoForPlacement,
} from "../services/promoService.firebase";
import PromoBanner from "../components/PromoBanner";
import { useCart } from "../context/CartContext";

/** Petit Card minimaliste pour éviter les dépendances */
function MiniProductCard({ p, onAdd }) {
    const price = Math.round(Number(p.price || 0)).toLocaleString("fr-FR");
    const slug = p.slug || p.id;
    return (
        <div className="card h-100 shadow-sm">
            {p.images?.[0] ? (
                <img
                    className="card-img-top"
                    src={p.images[0]}
                    alt={p.title || "Produit"}
                    style={{ height: 180, objectFit: "contain" }}
                />
            ) : (
                <div className="card-img-top bg-light" style={{ height: 180 }} />
            )}
            <div className="card-body d-flex flex-column">
                <h6 className="mb-1">{p.title || "Produit"}</h6>
                <div className="text-muted small mb-2">{price} FCFA</div>
                <div className="mt-auto d-flex gap-2">
                    <Link className="btn btn-outline-secondary btn-sm" to={`/p/${encodeURIComponent(slug)}`}>
                        Voir
                    </Link>
                    <button className="btn btn-primary btn-sm" onClick={() => onAdd(p, 1)}>
                        Ajouter
                    </button>
                </div>
            </div>
        </div>
    );
}
function PromoValidity({ promo }) {
    if (!promo) return null;
    const start = promo.startsAt ? new Date(promo.startsAt) : null;
    const end = promo.endsAt ? new Date(promo.endsAt) : null;

    let text = "Prix promotionnels valables";
    if (start && end) {
        text += ` du ${start.toLocaleDateString("fr-FR")} au ${end.toLocaleDateString("fr-FR")}`;
    } else if (end) {
        text += ` jusqu’au ${end.toLocaleDateString("fr-FR")}`;
    } else if (start) {
        text += ` à partir du ${start.toLocaleDateString("fr-FR")}`;
    }

    return (
        <div className="py-2 small mb-3 text-center" style={{fontWeight:"bold"}}>
            {`${text}.`}
        </div>
    );
}

export default function PromoPage() {
    const [promo, setPromo] = useState(null);
    const [loadingPromo, setLoadingPromo] = useState(true);
    const [items, setItems] = useState([]);
    const [loadingItems, setLoadingItems] = useState(true);
    const [error, setError] = useState("");

    const { addItem } = useCart();
    const navigate = useNavigate();
    const { search } = useLocation();

    // Ajout panier (simple – si variantes, on redirige vers la fiche)
    const handleAdd = useCallback((p, qty = 1) => {
        const hasVariants = Array.isArray(p.variants) && p.variants.length > 0;
        if (hasVariants) {
            const slug = p.slug || p.id;
            if (slug) navigate(`/p/${slug}`);
            return;
        }
        if (typeof p.stock === "number" && p.stock <= 0) return;
        addItem({
            productId: p.id || p.slug,
            variantKey: "default",
            variant: null,
            title: p.title || "Produit",
            price: Number(p.price || 0),
            image: p.images?.[0] || "",
            qty: Math.max(1, Number(qty) || 1),
        });
    }, [addItem, navigate]);

    // 1) Charger la promo (active / id / placement)
    useEffect(() => {
        (async () => {
            setLoadingPromo(true);
            setError("");
            try {
                const sp = new URLSearchParams(search);
                const id = sp.get("id");
                const placement = sp.get("placement");

                let p = null;
                if (id) p = await getPromo(id);
                else if (placement) p = await getActivePromoForPlacement(placement);
                else p = await getActivePromo();

                if (!p) {
                    setPromo(null);
                    setError("Aucune promotion n’est disponible.");
                } else {
                    setPromo(p);
                }
            } catch (e) {
                setPromo(null);
                setError(e?.message || "Impossible de charger la promotion.");
            } finally {
                setLoadingPromo(false);
            }
        })();
    }, [search]);

    // 2) Charger les produits de la promo (ordre conservé)
    useEffect(() => {
        (async () => {
            setLoadingItems(true);
            try {
                if (!promo?.productIds?.length) {
                    setItems([]);
                    return;
                }
                // Récupère chaque doc produit par id (préserve l’ordre)
                const fetched = [];
                for (const pid of promo.productIds) {
                    try {
                        const snap = await getDoc(doc(db, "products", String(pid)));
                        if (snap.exists()) {
                            const d = snap.data();
                            // on masque les produits inactifs
                            if (!d || (d.status && d.status !== "active")) continue;
                            fetched.push({ id: snap.id, ...d });
                        }
                    } catch { /* ignorer produit invalide */ }
                }
                setItems(fetched.slice(0, Math.max(1, Number(promo.limitProducts || 10))));
            } finally {
                setLoadingItems(false);
            }
        })();
    }, [promo]);

    return (
        <div className="container py-3 py-md-4">
            {/* Titre / fil d’ariane minimal */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h1 className="h5 m-0">Produits en promotion</h1>
                <Link className="text-decoration-none small" to="/catalogue">← Retour au catalogue</Link>
            </div>

            {/* Bannière promo */}
            {loadingPromo ? (
                <div className="rounded-3 overflow-hidden shadow-sm mb-3" style={{ minHeight: 160, background: "#f3f4f6" }} />
            ) : promo ? (
                <PromoBanner promo={promo} />
            ) : null}

            {/* Erreur / vide */}
            {error && <div className="alert alert-warning">{error}</div>}
            {promo && <PromoValidity promo={promo} />}
            {/* Produits */}
            {loadingItems ? (
                <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 g-2 g-md-3">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div className="col" key={`sk-${i}`}>
                            <div className="card h-100 shadow-sm">
                                <div className="card-img-top bg-light" style={{ height: 180 }} />
                                <div className="card-body">
                                    <span className="placeholder col-7" />
                                    <div className="mt-2"><span className="placeholder col-9" /></div>
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <span className="placeholder col-4" />
                                        <span className="placeholder col-3" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : items.length === 0 ? (
                <div className="alert alert-light">Aucun produit dans cette promotion.</div>
            ) : (
                <div className="row row-cols-2 row-cols-sm-3 row-cols-md-4 g-2 g-md-3">
                    {items.map((p) => (
                        <div className="col" key={p.id || p.slug}>
                            <MiniProductCard p={p} onAdd={handleAdd} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
